// The arithmetic behind the Cemetery summary and data check. Pure functions
// over the three record shapes, so every rule here has a test and the screen
// only lays the answers out.

export interface PlotLike {
  rawId?: number;
  recordId?: string;
  section?: string;
  plotNumber?: string;
  /** Availability: Available / Reserved / Occupied / Under Maintenance. */
  status?: string;
  allottedTo?: string;
  dateAdded?: string;
}

export interface BurialLike {
  rawId?: number;
  recordId?: string;
  section?: string;
  plotNumber?: string;
  deceasedName?: string;
  date?: string;
  dateOfDeath?: string;
}

export interface MaintenanceLike {
  date?: string;
  area?: string;
  taskType?: string;
  cost?: unknown;
}

const key = (section?: string, plotNumber?: string) =>
  `${String(section || '').trim().toLowerCase()}||${String(plotNumber || '').trim().toLowerCase()}`;

const yearOf = (iso?: string): number | null => {
  const m = /^(\d{4})-\d{2}-\d{2}$/.exec(String(iso || ''));
  return m ? Number(m[1]) : null;
};

const monthOf = (iso?: string): string | null => {
  const m = /^(\d{4}-\d{2})-\d{2}$/.exec(String(iso || ''));
  return m ? m[1] : null;
};

export const amountOf = (v: unknown): number => parseFloat(String(v ?? 0)) || 0;

// --- Occupancy ---------------------------------------------------------------

export interface BlockOccupancy {
  block: string;
  total: number;
  available: number;
  reserved: number;
  occupied: number;
  maintenance: number;
  /** Occupied as a share of every plot in the block, 0-100. */
  occupiedPct: number;
}

/** One row per block, blocks sorted naturally (Block 2 before Block 10). */
export const occupancyByBlock = (plots: PlotLike[]): BlockOccupancy[] => {
  const rows = new Map<string, BlockOccupancy>();
  plots.forEach((p) => {
    const block = String(p.section || 'Unassigned');
    const row = rows.get(block) || { block, total: 0, available: 0, reserved: 0, occupied: 0, maintenance: 0, occupiedPct: 0 };
    row.total += 1;
    const s = String(p.status || 'Available');
    if (s === 'Available') row.available += 1;
    else if (s === 'Reserved') row.reserved += 1;
    else if (s === 'Occupied') row.occupied += 1;
    else row.maintenance += 1;
    rows.set(block, row);
  });
  return Array.from(rows.values())
    .map((r) => ({ ...r, occupiedPct: r.total ? Math.round((r.occupied / r.total) * 1000) / 10 : 0 }))
    .sort((a, b) => a.block.localeCompare(b.block, undefined, { numeric: true }));
};

// --- Burials over time --------------------------------------------------------

export interface YearCount { year: number; count: number }

/** Burials per calendar year (by burial date), oldest first; undated skipped. */
export const burialsByYear = (burials: BurialLike[]): YearCount[] => {
  const counts = new Map<number, number>();
  burials.forEach((b) => {
    const y = yearOf(b.date);
    if (y !== null) counts.set(y, (counts.get(y) || 0) + 1);
  });
  return Array.from(counts.entries()).map(([year, count]) => ({ year, count })).sort((a, b) => a.year - b.year);
};

/**
 * Average burials a year over the last `span` complete years before `now`
 * (this year is excluded — it is not finished). Null when there is no history.
 */
export const burialsPerYear = (burials: BurialLike[], now: Date = new Date(), span = 3): number | null => {
  const thisYear = now.getFullYear();
  const window = burialsByYear(burials).filter((y) => y.year < thisYear && y.year >= thisYear - span);
  if (window.length === 0) return null;
  const total = window.reduce((n, y) => n + y.count, 0);
  return Math.round((total / span) * 10) / 10;
};

/** How many years the open plots last at the recent burial rate. Null without a rate. */
export const yearsOfSpaceLeft = (availablePlots: number, perYear: number | null): number | null =>
  perYear === null || perYear <= 0 ? null : Math.round((availablePlots / perYear) * 10) / 10;

// --- Maintenance spend --------------------------------------------------------

export interface MonthTotal { month: string; total: number }
export interface NamedTotal { name: string; total: number; count: number }

export const maintenanceByMonth = (rows: MaintenanceLike[]): MonthTotal[] => {
  const totals = new Map<string, number>();
  rows.forEach((r) => {
    const m = monthOf(r.date);
    if (m) totals.set(m, (totals.get(m) || 0) + amountOf(r.cost));
  });
  return Array.from(totals.entries()).map(([month, total]) => ({ month, total })).sort((a, b) => a.month.localeCompare(b.month));
};

/** Spend grouped by a field (area or task type), biggest first. */
export const maintenanceBy = (rows: MaintenanceLike[], field: 'area' | 'taskType'): NamedTotal[] => {
  const totals = new Map<string, NamedTotal>();
  rows.forEach((r) => {
    const name = String(r[field] || 'Unspecified').trim() || 'Unspecified';
    const row = totals.get(name) || { name, total: 0, count: 0 };
    row.total += amountOf(r.cost);
    row.count += 1;
    totals.set(name, row);
  });
  return Array.from(totals.values()).sort((a, b) => b.total - a.total);
};

/** Spend in one calendar year. */
export const maintenanceInYear = (rows: MaintenanceLike[], year: number): number =>
  rows.filter((r) => yearOf(r.date) === year).reduce((n, r) => n + amountOf(r.cost), 0);

// --- Data check ------------------------------------------------------------------

export interface OrphanBurial {
  burial: BurialLike;
  /** Live plots with the same number in another block — the likely intended one. */
  candidates: PlotLike[];
}

/**
 * Burials whose block + plot number match no live plot. They are invisible on
 * the map, and the plot they meant can be sold again. Plots with the same
 * number elsewhere are offered as the probable fix.
 */
export const orphanBurials = (plots: PlotLike[], burials: BurialLike[]): OrphanBurial[] => {
  const live = new Set(plots.map((p) => key(p.section, p.plotNumber)));
  return burials
    .filter((b) => !live.has(key(b.section, b.plotNumber)))
    .map((burial) => ({
      burial,
      candidates: plots.filter((p) =>
        String(p.plotNumber || '').trim().toLowerCase() === String(burial.plotNumber || '').trim().toLowerCase()
        && p.status !== 'Occupied')
    }));
};

/** Plots holding more than one live burial — a grave cannot be used twice. */
export const doubleBookedPlots = (burials: BurialLike[]): Array<{ section?: string; plotNumber?: string; burials: BurialLike[] }> => {
  const byPlot = new Map<string, BurialLike[]>();
  burials.forEach((b) => {
    const k = key(b.section, b.plotNumber);
    byPlot.set(k, [...(byPlot.get(k) || []), b]);
  });
  return Array.from(byPlot.values())
    .filter((list) => list.length > 1)
    .map((list) => ({ section: list[0].section, plotNumber: list[0].plotNumber, burials: list }));
};

// --- Follow-ups ------------------------------------------------------------------

export interface WaitingReservation { plot: PlotLike; years: number }

/**
 * Plots reserved for someone longer than `years` with no burial. The plot
 * table keeps only the date the plot was cut, so that stands in for the
 * booking date — an upper bound, never an understatement.
 */
export const reservedAndWaiting = (plots: PlotLike[], now: Date = new Date(), years = 2): WaitingReservation[] =>
  plots
    .filter((p) => p.status === 'Reserved' && p.dateAdded)
    .map((plot) => {
      const since = new Date(String(plot.dateAdded));
      const age = isNaN(since.getTime()) ? 0 : (now.getTime() - since.getTime()) / (365.25 * 24 * 3600 * 1000);
      return { plot, years: Math.round(age * 10) / 10 };
    })
    .filter((r) => r.years >= years)
    .sort((a, b) => b.years - a.years);

export interface Memorial { burial: BurialLike; yearsAgo: number; day: number }

/** Death anniversaries falling in a given month (1-12), soonest day first. */
export const memorialsInMonth = (burials: BurialLike[], month: number, now: Date = new Date()): Memorial[] =>
  burials
    .map((burial) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(burial.dateOfDeath || ''));
      if (!m || Number(m[2]) !== month) return null;
      return { burial, yearsAgo: now.getFullYear() - Number(m[1]), day: Number(m[3]) };
    })
    .filter((x): x is Memorial => x !== null && x.yearsAgo >= 0)
    .sort((a, b) => a.day - b.day);
